// React and Router
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

// Icons
import { IoArrowBackOutline } from 'react-icons/io5'

// Config and Constants
import { Permission } from '../../config/Permission'

// Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

// Components
import CustomToast from '../../components/Custom/CustomToast'
import PageHeader from '../../components/Custom/PageHeader/PageHeader'

const RoleAdd = () => {
  // Hooks
  const navigate = useNavigate()
  const goBack = useGoBack()
  const userPermissions = useUserStore((state) => state.user) || []

  // State
  const [formData, setFormData] = useState({
    roleName: '',
    permissions: [],
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Handlers
  const handleRoleNameChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      roleName: e.target.value,
    }))
    setError(null)
  }

  const togglePermission = (value) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(value)
        ? prev.permissions.filter((p) => p !== value)
        : [...prev.permissions, value],
    }))
    setError(null)
  }

  const validateForm = () => {
    if (!formData.roleName.trim()) {
      setError('Role name is required.')
      return false
    }

    if (formData.permissions.length === 0) {
      setError('At least one permission is required.')
      return false
    }

    return true
  }

  const handleSubmit = async () => {
    try {
      if (!userPermissions.includes('role:create')) {
        CustomToast({
          message: 'You do not have permission to create roles.',
          type: 'error',
        })
        return
      }

      if (!validateForm()) return

      setIsLoading(true)
      setError(null)

      const response = await axiosInterceptorInstance.post('/roles', {
        roleName: formData.roleName,
        permissions: formData.permissions,
      })

      if (response.data) {
        CustomToast({
          message: 'Role created successfully.',
          type: 'success',
        })
        navigate('/role')
      }
    } catch (err) {
      const errorMessage =
        err.status === 422
          ? 'Role has already been created.'
          : 'Failed to create a new role. Please try again!'

      setError(errorMessage)
      CustomToast({
        message: errorMessage,
        type: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={goBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Add Role</h1>
          </div>
        </div>
      </PageHeader>

      <div className='w-full sm:w-[500px] mx-auto'>
        <label className='block text-sm font-medium mb-2'>Role Name:</label>
        <input
          type='text'
          placeholder='Enter Role Name'
          className='w-full border-[2px] border-light-gray focus:border-prime rounded-lg p-3 focus:outline-none focus:border-orange invalid:border-red-700'
          value={formData.roleName}
          onChange={handleRoleNameChange}
        />

        {Object.entries(Permission).map(([category, perms]) => (
          <div key={category} className='my-4 p-2'>
            <h3 className='font-medium mb-2'>{category}</h3>
            <div className='flex space-x-6'>
              {Object.entries(perms).map(([action, value]) => (
                <label key={value} className='space-x-1 flex items-center'>
                  <input
                    type='checkbox'
                    className='w-5 h-5 accent-[#EF4044] cursor-pointer'
                    checked={formData.permissions.includes(value)}
                    onChange={() => togglePermission(value)}
                  />
                  <span>{action}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        {error && <p className='text-red-500 text-sm my-4'>{error}</p>}

        <button
          disabled={isLoading}
          onClick={handleSubmit}
          className='w-full py-3 rounded-md sm:w-[500px] text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-prime disabled:pointer-events-none disabled:cursor-auto disabled:bg-opacity-80 disabled:text-white'
        >
          Add Role
        </button>
      </div>
    </div>
  )
}

export default RoleAdd
